/**
 * Custom Select - Converts native <select> elements to styled dropdowns
 * Matches the cleaner search autocomplete styling with grey hover
 * Full keyboard navigation: Arrow keys, Enter, Escape, Home, End, type-to-search
 */

/**
 * Initialise the custom-select combobox on every ``<select.form-select>``
 * inside ``container`` (defaults to ``document``).
 *
 * Idempotent: existing wrappers inside ``container`` are torn down and
 * rebuilt so re-running the function never produces duplicate combobox
 * instances. Selects OUTSIDE ``container`` are left untouched, which
 * matters for partial swaps (e.g. the visit detail drawer's
 * ``_breaks_list.html`` re-render): without a scope, a global rebuild
 * would destroy and recreate every other combobox on the page,
 * dropping focus and producing a visible flash on filters/forms that
 * happen to also be open.
 *
 * The companion floating dropdowns live in <body> (not inside the
 * source select's wrapper) and are looked up by the listbox id stored
 * on the wrapped select — so the cleanup always finds and removes
 * them too, even when ``container`` is a small subtree.
 */
export function initCustomSelects(container) {
    // Defensive: an earlier bootstrap path bound this function
    // straight to ``addEventListener('DOMContentLoaded', ...)``, which
    // calls handlers with an Event as the first argument. ``Event``
    // has no ``querySelectorAll``, so naively trusting ``container``
    // crashed the page. Accept only Document or Element here; fall
    // back to ``document`` for anything else.
    const root = (container && typeof container.querySelectorAll === 'function')
        ? container
        : document;

    root.querySelectorAll('.custom-select-wrapper').forEach(function(wrapper) {
        var sel = wrapper.querySelector('select');
        if (sel) {
            sel.removeAttribute('data-custom-select');
            wrapper.parentNode.insertBefore(sel, wrapper);
        }
        var lbId = wrapper.dataset.customSelectListbox;
        if (lbId) {
            var dd = document.getElementById(lbId);
            if (dd) dd.remove();
        }
        wrapper.remove();
    });

    root.querySelectorAll('select.form-select').forEach(function(select) {
        createCustomSelect(select);
    });
}

function createCustomSelect(select) {
    select.dataset.customSelect = 'true';

    // Size variant — propagated from the source <select>'s Tailwind size
    // class so the generated combobox visually matches the surrounding
    // form controls. Without this, dropping a custom-select into a
    // compact form (e.g. the Add Break sub-form where every input is
    // ``text-xs py-1``) used to render a button ~33% taller than its
    // siblings and break grid alignment.
    //
    // Convention:
    //   <select class="form-select text-xs ...">   → compact
    //   <select class="form-select ...">           → standard (default)
    //
    // We deliberately key off ``text-xs`` only — adding more breakpoints
    // (text-base, text-lg) is YAGNI until a real use case appears.
    const isCompact = select.classList.contains('text-xs');

    const SIZE_BUTTON_CLS = isCompact ? 'text-xs px-2 py-1' : 'text-sm px-2.5 py-2';
    const SIZE_OPTION_CLS = isCompact ? 'text-xs px-2 py-1' : 'text-sm px-3 py-2';
    const ARROW_SIZE_CLS = isCompact ? 'w-3 h-3' : 'w-4 h-4';
    const ARROW_PAD_RIGHT = isCompact ? '1.5rem' : '2rem';

    const wrapper = document.createElement('div');
    wrapper.className = 'relative custom-select-wrapper';
    select.parentNode.insertBefore(wrapper, select);
    wrapper.appendChild(select);

    const listboxId = 'csel-lb-' + Math.random().toString(36).slice(2, 8);
    wrapper.dataset.customSelectListbox = listboxId;

    const button = document.createElement('button');
    button.type = 'button';
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.className = `bg-white text-gray-900 ${SIZE_BUTTON_CLS} rounded-lg focus:ring-0 focus:outline-none focus:border-primary-500 block w-full border border-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:text-white`;
    button.style.cssText = `display: flex !important; align-items: center !important; justify-content: space-between !important; gap: 0.5rem !important; padding-right: ${ARROW_PAD_RIGHT} !important;`;

    const selectedOption = select.options[select.selectedIndex];
    const displayText = selectedOption ? selectedOption.text : '';

    button.innerHTML = `
        <span class="custom-select-text truncate text-left">${escapeHtml(displayText)}</span>
        <svg class="${ARROW_SIZE_CLS} flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
        </svg>
    `;
    wrapper.appendChild(button);

    const dropdown = document.createElement('div');
    dropdown.setAttribute('role', 'listbox');
    dropdown.id = listboxId;
    dropdown.className = 'custom-select-dropdown hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700';
    dropdown.style.cssText = 'position:fixed; z-index:99999; border-radius:0.5rem; box-shadow:0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -4px rgba(0,0,0,.1); max-height:15rem; overflow-y:auto;';
    button.setAttribute('aria-controls', listboxId);

    let highlightedIndex = -1;
    let typeSearchStr = '';
    let typeSearchTimer = null;

    const HIGHLIGHT_CLS = ['bg-primary-50', 'dark:bg-primary-900/30'];
    // Selected option: weight only — no background tint, so the only
    // tinted row in an open dropdown is the one the user is currently
    // hovering / arrow-keying through (HIGHLIGHT_CLS).
    const SELECTED_CLS = ['font-medium'];

    function getOptionEls() {
        return Array.from(dropdown.querySelectorAll('[data-index]'));
    }

    function clearHighlight() {
        getOptionEls().forEach(el => {
            el.classList.remove(...HIGHLIGHT_CLS);
            el.removeAttribute('aria-current');
        });
    }

    function setHighlight(index) {
        const options = getOptionEls();
        if (index < 0 || index >= options.length) return;
        clearHighlight();
        highlightedIndex = index;
        const el = options[index];
        el.classList.add(...HIGHLIGHT_CLS);
        el.setAttribute('aria-current', 'true');
        button.setAttribute('aria-activedescendant', el.id);
        el.scrollIntoView({ block: 'nearest' });
    }

    function rebuildOptions() {
        let html = '';
        Array.from(select.options).forEach((option, index) => {
            // PR 2.2-quater follow-up (PO 2026-05-21) — honour
            // ``<option hidden>`` from the underlying native select.
            // The HTML spec (and every modern browser since 2017) hides
            // ``<option hidden>`` from the native dropdown entirely
            // (not greyed — *absent*). The first consumer was the
            // ``customer-form.js::syncPaymentOptions`` hook which hides
            // CASH and CARD when ``customer_type=COMPANY`` ; before
            // this fix the two options stayed visible-but-greyed in
            // the dropdown because the rebuild only checked
            // ``option.disabled``, producing the PO-reported « pourquoi
            // Carte est grisé pour entreprise ? » surprise. Skipping
            // hidden options here aligns the custom-select with the
            // native semantic — any future hide/show pattern (CIaO
            // service-type filter, contractor-only payment plans,
            // …) gets the right behaviour for free.
            if (option.hidden) return;
            const isSelected = option.selected;
            // Slice F.20 follow-up — honour ``<option disabled>`` from
            // the underlying native select. The standard semantic is
            // "this row is non-selectable"; the native browser
            // dropdown greys it out and refuses click + keyboard
            // navigation. Custom-select must mirror that contract or
            // every consumer that disables an option (job-form's
            // movable-property gate, future similar greying patterns)
            // would render a "click works but value snaps back" UX
            // surprise.
            const isDisabled = option.disabled;
            // PO request 2026-05-15 12:01: ``cursor-not-allowed``
            // (the "🚫" icon) felt aggressive on a row that's
            // simply unavailable in the current context (worksite ↔
            // service type incompatibility). Use ``cursor-default``
            // (the OS arrow) so the visual greying carries the
            // signal without the warning iconography. Hover tint is
            // still suppressed so the row visibly reads as inert.
            const stateCls = isDisabled
                ? 'opacity-60 cursor-default text-gray-500 dark:text-gray-400'
                : `hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer text-gray-900 dark:text-white ${isSelected ? SELECTED_CLS.join(' ') : ''}`;
            const optId = listboxId + '-opt-' + index;
            const ariaDisabledAttr = isDisabled ? ' aria-disabled="true"' : '';
            html += `
                <div role="option"
                     id="${optId}"
                     aria-selected="${isSelected}"${ariaDisabledAttr}
                     class="${SIZE_OPTION_CLS} ${stateCls}"
                     data-value="${escapeHtml(option.value)}"
                     data-index="${index}">
                    ${escapeHtml(option.text)}
                </div>
            `;
        });
        dropdown.innerHTML = html;

        const sel = select.options[select.selectedIndex];
        const text = sel ? sel.text : '';
        button.querySelector('.custom-select-text').textContent = text;

        button.disabled = select.disabled;
        button.style.opacity = select.disabled ? '0.6' : '1';
        button.style.cursor = select.disabled ? 'default' : 'pointer';
    }

    rebuildOptions();
    document.body.appendChild(dropdown);

    // Slice F.20 follow-up — extend the MutationObserver to fire on:
    //   • ``childList`` on the <select>: option add / remove
    //   • ``attributes`` (filtered): ``disabled`` + ``hidden`` flips on
    //     the <select> itself AND on every <option> in the subtree.
    //     ``disabled``: job-form's movable-property gate per row.
    //     ``hidden``  : customer-form's preferred_payment_method sync
    //                   hook (PR 2.2-quater) per option.
    //   • ``characterData: true``: live label edit (the job-form gate
    //     appends « — incompatible with CIaO worksite » to the
    //     option's textContent).
    // Without ``subtree`` the per-option mutations were invisible and
    // the dropdown stayed stale until the next full rebuild. The
    // ``attributeFilter`` whitelist intentionally stays narrow (2
    // attributes) so the observer does not fire on noisy intrinsic
    // option mutations (``selected``, ``value``, …) during the native
    // browser's option-list parse.
    const optionsObserver = new MutationObserver(rebuildOptions);
    optionsObserver.observe(select, {
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'hidden'],
        subtree: true,
        characterData: true,
    });

    function positionDropdown() {
        const rect = button.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = dropdown.scrollHeight;

        if (spaceBelow < dropdownHeight && rect.top > spaceBelow) {
            dropdown.style.top = '';
            dropdown.style.bottom = (window.innerHeight - rect.top + 4) + 'px';
        } else {
            dropdown.style.bottom = '';
            dropdown.style.top = (rect.bottom + 4) + 'px';
        }
        dropdown.style.left = rect.left + 'px';
        dropdown.style.width = rect.width + 'px';
    }

    function isOpen() {
        return !dropdown.classList.contains('hidden');
    }

    function openDropdown() {
        document.querySelectorAll('.custom-select-dropdown:not(.hidden)').forEach(d => {
            if (d !== dropdown) d.classList.add('hidden');
        });

        // Make the dropdown measurable BEFORE positionDropdown reads
        // its scrollHeight. The Tailwind ``.hidden`` class is
        // ``display: none`` which collapses scrollHeight to 0; running
        // positionDropdown in that state always takes the "open
        // downward" branch even when the button is near the bottom of
        // the viewport (its flip-up condition compares dropdownHeight
        // to spaceBelow). The result was a dropdown rendered at
        // ``top = button.bottom + 4`` that fell off-screen — observed
        // on the EndClient inline modal's "Pays" picker (PO report
        // 2026-05-19, modal panel ``max-h-[calc(100vh-3rem)]`` placed
        // the address sub-block's country select near the viewport
        // bottom; the country list extended below the visible area
        // and could not be scrolled to).
        //
        // ``visibility: hidden`` keeps the element in the layout (so
        // scrollHeight reflects the real rendered content) while
        // remaining invisible — no paint flash in the gap between
        // unhiding and positioning. The inline style is cleared in
        // ``closeDropdown`` defensively so a re-open also pre-measures
        // correctly even if a previous open path left it dirty.
        dropdown.style.visibility = 'hidden';
        dropdown.classList.remove('hidden');
        positionDropdown();
        dropdown.style.visibility = '';

        button.setAttribute('aria-expanded', 'true');
        // Track the keyboard cursor at the currently-selected option so
        // ArrowDown / ArrowUp move from there, but DO NOT paint the
        // highlight tint yet — the primary-50 background should only
        // show once the user actively arrow-keys (or hovers via CSS).
        // The bold weight on the selected option (SELECTED_CLS) is
        // enough to mark "this is your current value".
        highlightedIndex = select.selectedIndex;
    }

    function closeDropdown() {
        dropdown.classList.add('hidden');
        // Defensive reset: openDropdown temporarily sets
        // ``visibility: hidden`` to measure scrollHeight (see comment
        // there). If openDropdown bailed out between the unhide and
        // the restore (e.g. a positionDropdown throw), the next open
        // would still find ``visibility: hidden`` and paint nothing
        // even though ``.hidden`` is removed. Clearing it on close
        // makes the recovery automatic.
        dropdown.style.visibility = '';
        button.setAttribute('aria-expanded', 'false');
        button.removeAttribute('aria-activedescendant');
        clearHighlight();
        highlightedIndex = -1;
    }

    function selectIndex(index) {
        const options = getOptionEls();
        if (index < 0 || index >= options.length) return;
        // Slice F.20 follow-up — refuse to commit a disabled option.
        // Mirrors the native <select> behaviour and prevents both
        // accidental clicks and Enter-key activations from
        // smuggling a value past the gate.
        if (select.options[index] && select.options[index].disabled) return;

        select.selectedIndex = index;
        select.dispatchEvent(new Event('change', { bubbles: true }));

        const text = select.options[index] ? select.options[index].text : '';
        button.querySelector('.custom-select-text').textContent = text;

        options.forEach(opt => opt.classList.remove(...SELECTED_CLS));
        options[index].classList.add(...SELECTED_CLS);
        options.forEach((opt, i) => opt.setAttribute('aria-selected', String(i === index)));

        closeDropdown();

        // Note: the dispatchEvent('change', { bubbles: true }) call
        // above is the canonical way to notify any listener (direct
        // or delegated) that the underlying <select> has changed.
        // schedule-calendar.js attaches a direct ``change`` listener
        // on ``#status-filter`` that catches it. We deliberately do
        // NOT also call window.applyCalendarFilters() here — the
        // explicit call was redundant with the dispatched event and
        // caused the schedule status pick to push ~3 history entries
        // per change (one per fan-out path). See the long comment in
        // schedule-calendar.js's status-filter wiring.
    }

    // Toggle dropdown on button click
    button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (select.disabled) return;
        if (isOpen()) closeDropdown(); else openDropdown();
    });

    // Handle option click
    dropdown.addEventListener('click', (e) => {
        const optionEl = e.target.closest('[data-index]');
        if (!optionEl) return;
        selectIndex(parseInt(optionEl.dataset.index));
    });

    // Slice F.20 follow-up — keyboard navigation must skip
    // ``<option disabled>`` entries the same way the native
    // <select> dropdown does, otherwise a user can land the
    // highlight on a non-selectable row, hit Enter, and get
    // confused when nothing happens.
    function nextEnabled(from, dir) {
        const opts = select.options;
        const n = opts.length;
        if (n === 0) return -1;
        let i = from;
        for (let step = 0; step < n; step++) {
            i += dir;
            if (i < 0 || i >= n) return from >= 0 && !opts[from].disabled ? from : -1;
            if (!opts[i].disabled) return i;
        }
        return from >= 0 && !opts[from].disabled ? from : -1;
    }

    function firstEnabled() {
        const opts = select.options;
        for (let i = 0; i < opts.length; i++) if (!opts[i].disabled) return i;
        return -1;
    }

    function lastEnabled() {
        const opts = select.options;
        for (let i = opts.length - 1; i >= 0; i--) if (!opts[i].disabled) return i;
        return -1;
    }

    // Keyboard navigation on the button
    button.addEventListener('keydown', (e) => {
        if (select.disabled) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                if (!isOpen()) {
                    openDropdown();
                } else {
                    const target = nextEnabled(highlightedIndex, 1);
                    if (target >= 0) setHighlight(target);
                }
                break;

            case 'ArrowUp':
                e.preventDefault();
                if (!isOpen()) {
                    openDropdown();
                } else {
                    const target = nextEnabled(highlightedIndex, -1);
                    if (target >= 0) setHighlight(target);
                }
                break;

            case 'Home':
                if (isOpen()) {
                    e.preventDefault();
                    const target = firstEnabled();
                    if (target >= 0) setHighlight(target);
                }
                break;

            case 'End':
                if (isOpen()) {
                    e.preventDefault();
                    const target = lastEnabled();
                    if (target >= 0) setHighlight(target);
                }
                break;

            case 'Enter':
            case ' ':
                e.preventDefault();
                if (isOpen() && highlightedIndex >= 0) {
                    selectIndex(highlightedIndex);
                } else {
                    openDropdown();
                }
                break;

            case 'Escape':
                if (isOpen()) {
                    e.preventDefault();
                    e.stopPropagation();
                    closeDropdown();
                    button.focus();
                }
                break;

            case 'Tab':
                if (isOpen()) {
                    closeDropdown();
                }
                break;

            default:
                // Type-to-search: jump to first option starting with typed character(s)
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                    e.preventDefault();
                    typeSearchStr += e.key.toLowerCase();
                    clearTimeout(typeSearchTimer);
                    typeSearchTimer = setTimeout(() => { typeSearchStr = ''; }, 500);

                    if (!isOpen()) openDropdown();

                    const opts = Array.from(select.options);
                    const startIdx = typeSearchStr.length === 1 ? highlightedIndex + 1 : 0;
                    for (let i = 0; i < opts.length; i++) {
                        const idx = (startIdx + i) % opts.length;
                        // Slice F.20 follow-up — type-to-search
                        // must also ignore disabled options.
                        if (opts[idx].disabled) continue;
                        if (opts[idx].text.toLowerCase().startsWith(typeSearchStr)) {
                            setHighlight(idx);
                            break;
                        }
                    }
                }
                break;
        }
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
        if (!wrapper.contains(e.target) && !dropdown.contains(e.target)) {
            closeDropdown();
        }
    });

    // Close on outside scroll, reposition on resize
    dropdown.addEventListener('wheel', (e) => {
        const atTop = dropdown.scrollTop === 0;
        const atBottom = dropdown.scrollTop + dropdown.clientHeight >= dropdown.scrollHeight;
        if ((e.deltaY < 0 && atTop) || (e.deltaY > 0 && atBottom)) {
            e.preventDefault();
        }
    }, { passive: false });
    window.addEventListener('scroll', (e) => {
        if (isOpen() && !dropdown.contains(e.target)) closeDropdown();
    }, true);
    window.addEventListener('resize', () => {
        if (isOpen()) positionDropdown();
    });

    // Sync display when select value changes programmatically
    select.addEventListener('change', () => {
        const sel = select.options[select.selectedIndex];
        const text = sel ? sel.text : '';
        button.querySelector('.custom-select-text').textContent = text;

        getOptionEls().forEach((opt, i) => {
            opt.classList.remove(...SELECTED_CLS);
            opt.setAttribute('aria-selected', 'false');
            if (i === select.selectedIndex) {
                opt.classList.add(...SELECTED_CLS);
                opt.setAttribute('aria-selected', 'true');
            }
        });
    });

    // Clean up when wrapper is removed from the DOM
    const cleanupObserver = new MutationObserver(() => {
        if (!document.body.contains(wrapper)) {
            dropdown.remove();
            optionsObserver.disconnect();
            cleanupObserver.disconnect();
        }
    });
    cleanupObserver.observe(document.body, { childList: true, subtree: true });
}

function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Auto-init on DOM ready. The arrow wrapper is required: passing
// ``initCustomSelects`` directly would call it with the
// DOMContentLoaded Event as its first arg, which now matters since
// the function reads ``container.querySelectorAll`` (the defensive
// guard above logs nothing — the wrapper just keeps the call site
// honest about intent).
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initCustomSelects());
} else {
    initCustomSelects();
}

window.initCustomSelects = initCustomSelects;
